import type { RefObject } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

export type UseIntersectionObserverReturn = IntersectionObserverEntry | null;

type IntersectionObserverOptions = { root: RefObject<Element | Document | undefined | null>; threshold: number };
export function useIntersectionObserver({
	root,
	threshold,
}: IntersectionObserverOptions): UseIntersectionObserverReturn {
	const observerRef = useRef<IntersectionObserver | null>(null);
	const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);

	const isIntersectionObserverSupported = useMemo(
		() => typeof window !== 'undefined' && 'IntersectionObserver' in window,
		[],
	);

	const disconnect = () => {
		if (!observerRef.current) return;

		observerRef.current.disconnect();
		observerRef.current = null;
	};

	useEffect(() => {
		if (!isIntersectionObserverSupported || !root.current) return undefined;

		const handleIntersection: IntersectionObserverCallback = (observerEntries) => {
			observerEntries.forEach((observerEntry) => {
				if (observerEntry.isIntersecting) {
					setEntry(observerEntry);
				}
			});
		};

		const container = root.current;
		const observer = new IntersectionObserver(handleIntersection, { root: container, threshold });

		/** Observing an element twice is a no-op, so new children can simply be added. */
		const observeChildren = () => {
			Array.from(container.children).forEach((child) => {
				observer.observe(child);
			});
		};
		observeChildren();

		// The items can change after mount (ScrollContainerV2 keys them by count, so removing one, e.g. a
		// deleted supporter video, remounts them all). Re-observe the current children and drop an entry
		// whose target left the DOM, or the arrows would keep measuring a detached element.
		const mutations =
			typeof MutationObserver === 'undefined'
				? null
				: new MutationObserver(() => {
						observeChildren();
						setEntry((current) => (current && !current.target.isConnected ? null : current));
					});
		mutations?.observe(container, { childList: true });

		observerRef.current = observer;

		return () => {
			disconnect();
			mutations?.disconnect();
		};
	}, [isIntersectionObserverSupported, root, threshold]);

	return entry;
}
